/**
 * gitClient — main-thread wrappers for the wasm-git worker's read/restore
 * ops (T3.21 version history). Mirrors publishSite's one-shot worker
 * lifecycle: spawn → post `{ op }` (after the worker's `ready` ping) →
 * resolve → terminate.
 *
 * History is sourced from the site's git log (each publish is a commit);
 * restore creates a new commit carrying an old commit's tree and pushes
 * it (rollback = new deploy). Auth is owner-only server-side (the BFF git
 * proxy → workerd `requireOwner` on both fetch and push).
 */
import { withTimeout } from "./withTimeout.js";
import { classifyPublishError, type PublishError } from "./publishError.js";

/** One version on the timeline (a commit on the default branch). */
export interface VersionCommit {
  readonly oid: string;
  readonly summary: string;
  /** committer timestamp, epoch seconds. */
  readonly committedAt: number;
  /** present when this version was produced by a restore. */
  readonly restoredFrom?: string;
}

/**
 * A union, not `{ok, error?}`. The old shape admitted `{ok: true, error: …}`
 * and — the bug it actually caused — let a "successful" log arrive with no
 * head: `headOid: r.headOid ?? ""` then matched no commit, so EVERY version
 * silently rendered as not-live. A head is now part of what success MEANS.
 */
export type GitLogResult =
  | {
      readonly ok: true;
      readonly commits: ReadonlyArray<VersionCommit>;
      /** the live commit (branch tip). */
      readonly headOid: string;
    }
  | { readonly ok: false; readonly failure: PublishError };

export interface GitRestoreResult {
  readonly ok: boolean;
  readonly error?: string;
  /** a concurrent publish moved the tip — refresh + retry. */
  readonly rejected?: boolean;
  readonly newOid?: string;
}

export interface SnapshotResult {
  readonly ok: boolean;
  readonly error?: string;
  /** the version's exact editor snapshot, or null when the commit
   *  predates the snapshot feature (→ "preview not available"). */
  readonly snapshot: string | null;
}

interface RawResult {
  ok: boolean;
  error?: string;
  kind?: "push-rejected";
  commits?: VersionCommit[];
  headOid?: string;
  newOid?: string;
  snapshot?: string | null;
}

function remoteUrl(siteId: string): string {
  return `${location.origin}/api/git/${siteId}.git`;
}

/** Spawn the read worker (separate from the publish worker — see its
 * header), run one op, terminate. */
function runWorker(req: unknown): Promise<RawResult> {
  const worker = new Worker(new URL("./gitReadWorker.ts", import.meta.url), {
    type: "module",
  });
  const rpc = new Promise<RawResult>((resolve, reject) => {
    worker.onmessage = (e: MessageEvent) => {
      const data = e.data as { ready?: boolean } & RawResult;
      if (data?.ready) {
        worker.postMessage(req);
        return;
      }
      resolve(data);
    };
    worker.onerror = (e) => reject(new Error(e.message || "git worker error"));
    // `onerror` does not fire for a module that fails to parse/instantiate.
    worker.onmessageerror = () =>
      reject(new Error("git worker sent an undeserializable message"));
  });
  // Terminate on every path — including the timeout, where the worker is
  // wedged and would otherwise linger holding its OPFS handles.
  return withTimeout(rpc, "git read").finally(() => worker.terminate());
}

export async function gitLog(siteId: string): Promise<GitLogResult> {
  const r = await runWorker({
    op: "log",
    siteId,
    remoteUrl: remoteUrl(siteId),
  });
  // A log without a head is NOT a success — see GitLogResult.
  if (!r.ok || typeof r.headOid !== "string" || r.headOid === "")
    return {
      ok: false,
      failure: classifyPublishError({
        error: r.error ?? "the version history has no branch tip",
        ...(r.kind !== undefined ? { kind: r.kind } : {}),
      }),
    };
  return { ok: true, commits: r.commits ?? [], headOid: r.headOid };
}

/** Read a past version's exact editor snapshot for read-only preview.
 *  `snapshot: null` ⇒ the commit predates the feature (older version). */
export async function readSnapshot(
  siteId: string,
  commitOid: string,
): Promise<SnapshotResult> {
  const r = await runWorker({
    op: "read-snapshot",
    siteId,
    commitOid,
    remoteUrl: remoteUrl(siteId),
  });
  return {
    ok: r.ok,
    ...(r.error !== undefined ? { error: r.error } : {}),
    snapshot: r.snapshot ?? null,
  };
}

export async function gitRestore(
  siteId: string,
  commitOid: string,
): Promise<GitRestoreResult> {
  const r = await runWorker({
    op: "restore",
    siteId,
    commitOid,
    remoteUrl: remoteUrl(siteId),
  });
  return {
    ok: r.ok,
    ...(r.error !== undefined ? { error: r.error } : {}),
    ...(r.kind === "push-rejected" ? { rejected: true } : {}),
    ...(r.newOid !== undefined ? { newOid: r.newOid } : {}),
  };
}
